import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Info, CreditCard, RefreshCw, Banknote, QrCode, ArrowLeftRight, Layers } from "lucide-react";
import { getSalesOrders } from "../../api/salesService";

const formatCurrency = (val) => {
  const num = Number(val || 0);
  return `Rp ${num.toLocaleString("id-ID")}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNextDayDate = (dateStr) => {
  if (!dateStr) return dateStr;
  const parts = String(dateStr).split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const d = new Date(year, month, day + 1);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const dt = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${dt}`;
  }
  return dateStr;
};

const getPaymentMethodIcon = (method) => {
  const m = String(method || "").toUpperCase();
  if (m === "CASH") return Banknote;
  if (m === "QRIS") return QrCode;
  if (m === "TRANSFER") return ArrowLeftRight;
  if (m === "MIXED") return Layers;
  return CreditCard;
};

const getPaymentMethodLabel = (method) => {
  const m = String(method || "").toUpperCase();
  if (m === "CASH") return "Tunai (Cash)";
  if (m === "QRIS") return "QRIS";
  if (m === "TRANSFER") return "Transfer Bank";
  if (m === "MIXED") return "Campuran (Mixed / Split)";
  return method || "-";
};

const isOrderMatchingPaymentMethod = (ord, targetMethod) => {
  const target = String(targetMethod || "").trim().toUpperCase();
  
  if (Array.isArray(ord.payments) && ord.payments.length > 0) {
    const hasMatch = ord.payments.some((p) => {
      const pm = String(p.payment_method || "").trim().toUpperCase();
      if (target === "CASH") {
        return pm === "CASH" || (pm === "MIXED" && Number(p.cash_amount || 0) > 0);
      }
      return pm === target;
    });
    if (hasMatch) return true;
  }

  const directMethod = String(ord.payment_method || ord.paymentMethod || "").trim().toUpperCase();
  if (target === "CASH") {
    return directMethod === "CASH" || directMethod === "MIXED";
  }
  if (directMethod === target) return true;

  return false;
};

function PaymentExpandableRow({
  row,
  idx,
  startDate,
  endDate,
  onSelectTransaction,
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const paymentMethodKey = row.payment_method || "-";
  const displayLabel = getPaymentMethodLabel(paymentMethodKey);
  const MethodIcon = getPaymentMethodIcon(paymentMethodKey);

  useEffect(() => {
    if (expanded && !hasLoaded) {
      setLoading(true);
      setError(null);

      const effectiveEndDate = getNextDayDate(endDate);

      const fetchAllPages = async () => {
        const PAGE_LIMIT = 100;
        let page = 1;
        let allItems = [];
        try {
          while (page <= 50) {
            const res = await getSalesOrders({
              start_date: startDate,
              end_date: effectiveEndDate,
              status: "DONE,DELIVERED",
              page,
              limit: PAGE_LIMIT,
            });
            const list = res?.data ?? res ?? [];
            const items = Array.isArray(list) ? list : list?.items || list?.data || [];
            const meta = res?.meta ?? list?.meta ?? null;

            if (Array.isArray(items)) allItems = allItems.concat(items);

            const total = Number(meta?.total ?? 0);
            if (total > 0 && allItems.length >= total) break;
            if (!items || items.length === 0) break;
            page += 1;
          }

          const filtered = allItems.filter((item) =>
            isOrderMatchingPaymentMethod(item, paymentMethodKey)
          );

          setOrders(filtered);
          setHasLoaded(true);
        } catch (err) {
          setError(err.message || "Gagal memuat transaksi. Silakan periksa koneksi atau klik Refresh Data.");
        } finally {
          setLoading(false);
        }
      };

      fetchAllPages();
    }
  }, [expanded, hasLoaded, paymentMethodKey, startDate, endDate]);

  const totals = orders.reduce(
    (acc, ord) => {
      acc.subtotal += Number(ord.subtotal || ord.sub_total || 0);
      acc.discount += Number(ord.discount_total || ord.discount || 0);
      acc.additional += Number(ord.additional_costs || ord.additional_cost || 0);
      acc.grandTotal += Number(ord.grand_total || 0);
      return acc;
    },
    { subtotal: 0, discount: 0, additional: 0, grandTotal: 0 }
  );

  const PAGE_SIZE = 15;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visibleOrders = orders.slice(0, visibleCount);
  const hasMore = visibleCount < orders.length;
  const remaining = orders.length - visibleCount;

  return (
    <>
      <tr
        onClick={() => setExpanded((prev) => !prev)}
        className={`cursor-pointer transition-colors ${
          expanded ? "bg-teal-50/70 font-medium" : "hover:bg-slate-50/80"
        }`}>
        <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-semibold">
          {idx + 1}
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap font-bold text-slate-900">
          <div className="flex items-center gap-2.5">
            <span className="w-6 h-6 rounded-md bg-teal-100 text-teal-800 flex items-center justify-center flex-shrink-0">
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
            <div className="flex items-center gap-2">
              <MethodIcon className="w-4 h-4 text-teal-600" />
              <span>{displayLabel}</span>
              <span className="text-[11px] font-semibold text-slate-400">({paymentMethodKey})</span>
            </div>
          </div>
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-medium text-center">
          {row.transaction_count || 0} Transaksi
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap font-bold text-teal-700 text-right">
          {formatCurrency(row.total_sales || 0)}
        </td>
      </tr>

      {/* Expanded Lipatan Panel */}
      {expanded && (
        <tr>
          <td colSpan={4} className="p-0 bg-slate-50/60">
            <div className="p-4 border-y border-teal-200 bg-gradient-to-b from-teal-50/40 to-slate-50/80 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-teal-950">
                  <MethodIcon className="w-4 h-4 text-teal-600" />
                  <h4 className="font-bold text-sm">
                    Daftar Transaksi Metode Pembayaran: <span className="text-teal-700 font-extrabold">{displayLabel}</span>
                  </h4>
                  <span className="text-xs text-slate-500 font-normal">
                    ({startDate || "Awal"} s/d {endDate || "Sekarang"})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setHasLoaded(false)}
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-700 font-medium">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh Data
                </button>
              </div>

              {loading ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Memuat rincian transaksi metode {displayLabel}...
                </div>
              ) : error ? (
                <div className="py-8 text-center text-xs text-rose-600">{error}</div>
              ) : orders.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Tidak ada transaksi tercatat untuk metode "{displayLabel}" pada periode ini.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 bg-white shadow-sm">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-100/90 border-b border-slate-200 font-semibold text-slate-600 uppercase tracking-wider text-left">
                      <tr>
                        <th className="px-3.5 py-2.5">No. Transaksi</th>
                        <th className="px-3.5 py-2.5">Waktu</th>
                        <th className="px-3.5 py-2.5">Pelanggan</th>
                        <th className="px-3.5 py-2.5 text-right">Subtotal</th>
                        <th className="px-3.5 py-2.5 text-right">Diskon</th>
                        <th className="px-3.5 py-2.5 text-left">Rincian Bayar</th>
                        <th className="px-3.5 py-2.5 text-right">Grand Total</th>
                        <th className="px-3.5 py-2.5 text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleOrders.map((ord, oIdx) => {
                        const sub = Number(ord.subtotal || ord.sub_total || 0);
                        const disc = Number(ord.discount_total || ord.discount || 0);
                        const grand = Number(ord.grand_total || 0);

                        // Render detail pembayaran per order
                        const renderPaymentDetail = () => {
                          if (Array.isArray(ord.payments) && ord.payments.length > 0) {
                            return (
                              <div className="space-y-0.5">
                                {ord.payments.map((p, pIdx) => {
                                  const pMethod = String(p.payment_method || "").toUpperCase();
                                  const cashAmt = Number(p.cash_amount || 0);
                                  const elecAmt = Number(p.electronic_amount || 0);
                                  const change = Number(ord.change_amount || 0);

                                  if (pMethod === "MIXED") {
                                    return (
                                      <div key={p.id || pIdx} className="text-[11px] text-slate-600">
                                        <span className="font-semibold text-slate-800">Tunai:</span> {formatCurrency(cashAmt)} | <span className="font-semibold text-slate-800">Non-Tunai:</span> {formatCurrency(elecAmt)}
                                      </div>
                                    );
                                  }
                                  if (pMethod === "CASH") {
                                    return (
                                      <div key={p.id || pIdx} className="text-[11px] text-slate-600">
                                        <span className="font-semibold text-slate-800">Tunai:</span> {formatCurrency(cashAmt)}
                                        {change > 0 && <span className="text-slate-400"> (Kembali: {formatCurrency(change)})</span>}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={p.id || pIdx} className="text-[11px] text-slate-600">
                                      <span className="font-semibold text-slate-800">{pMethod}:</span> {formatCurrency(elecAmt || grand)}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          return <span className="text-slate-500">{displayLabel}</span>;
                        };

                        return (
                          <tr key={ord.id || oIdx} className="hover:bg-teal-50/50 transition-colors">
                            <td className="px-3.5 py-2.5 font-semibold text-slate-900 whitespace-nowrap">
                              {ord.transaction_number || "-"}
                            </td>
                            <td className="px-3.5 py-2.5 text-slate-500 whitespace-nowrap">
                              {formatDateTime(ord.created_at || ord.order_date)}
                            </td>
                            <td className="px-3.5 py-2.5 font-medium text-slate-700 whitespace-nowrap">
                              {ord.customer_name || ord.customer?.name || "Pelanggan Umum"}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-medium text-slate-700 whitespace-nowrap">
                              {formatCurrency(sub)}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-semibold text-rose-600 whitespace-nowrap">
                              {disc > 0 ? `- ${formatCurrency(disc)}` : "-"}
                            </td>
                            <td className="px-3.5 py-2.5 whitespace-nowrap">
                              {renderPaymentDetail()}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                              {formatCurrency(grand)}
                            </td>
                            <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => onSelectTransaction(ord)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-teal-50 text-teal-700 hover:bg-teal-100 font-semibold transition-colors ring-1 ring-teal-200">
                                <Info className="w-3.5 h-3.5" />
                                Detail
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Gradient blur + Lihat Selengkapnya */}
                  {hasMore && (
                    <div className="relative">
                      <div
                        className="absolute inset-x-0 bottom-full h-16 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-white border-t border-teal-100 text-teal-700 hover:bg-teal-50 text-xs font-semibold transition-colors group">
                        <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                        Lihat Selengkapnya
                        <span className="bg-teal-100 text-teal-800 font-bold px-2 py-0.5 rounded-full text-[10px]">
                          +{remaining} lagi
                        </span>
                      </button>
                    </div>
                  )}

                  {/* REKAP TOTAL */}
                  <div className="border-t-2 border-teal-300 bg-teal-100/60">
                    <table className="min-w-full text-xs">
                      <tfoot className="font-bold text-slate-900">
                        <tr>
                          <td colSpan={3} className="px-3.5 py-3 text-teal-950 uppercase tracking-wider text-[11px]">
                            REKAP TOTAL ({displayLabel}) — {orders.length} Transaksi
                          </td>
                          <td className="px-3.5 py-3 text-right text-slate-900 font-bold whitespace-nowrap">
                            {formatCurrency(totals.subtotal)}
                          </td>
                          <td className="px-3.5 py-3 text-right text-rose-600 font-extrabold whitespace-nowrap">
                            {totals.discount > 0 ? `- ${formatCurrency(totals.discount)}` : "-"}
                          </td>
                          <td></td>
                          <td className="px-3.5 py-3 text-right text-teal-900 font-extrabold text-sm whitespace-nowrap">
                            {formatCurrency(totals.grandTotal)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PaymentRecapTable({
  rows = [],
  loading = false,
  error = null,
  startDate,
  endDate,
  onSelectTransaction,
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold text-[11px] uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left w-16">No</th>
            <th className="px-4 py-3 text-left">Metode Pembayaran</th>
            <th className="px-4 py-3 text-center">Jumlah Transaksi</th>
            <th className="px-4 py-3 text-right">Total Pembayaran</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {loading && (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                Memuat rekap metode pembayaran...
              </td>
            </tr>
          )}

          {!loading && error && (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-rose-600">
                {error}
              </td>
            </tr>
          )}

          {!loading && !error && rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-12 text-center text-slate-400">
                Belum ada data transaksi pembayaran pada periode ini.
              </td>
            </tr>
          )}

          {!loading &&
            !error &&
            rows.map((row, idx) => (
              <PaymentExpandableRow
                key={row.payment_method || idx}
                row={row}
                idx={idx}
                startDate={startDate}
                endDate={endDate}
                onSelectTransaction={onSelectTransaction}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}
